import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { recruiterApi } from '../services/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Loader2 } from 'lucide-react'

export default function ReadyTestIntakePage() {
  const { inviteUuid } = useParams<{ inviteUuid: string }>()
  const navigate = useNavigate()
  const [jobDescription, setJobDescription] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [cv, setCv] = useState<File | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!inviteUuid) {
      setError('הקישור אינו תקין')
      setLoading(false)
      return
    }

    const loadInvite = async () => {
      try {
        const response = await recruiterApi.getReadyTestInvite(inviteUuid)
        if (response.invite.started && response.invite.sent_form_uuid) {
          navigate(`/candidate/${response.invite.sent_form_uuid}`, { replace: true })
          return
        }
        setJobDescription(response.invite.job_description || '')
      } catch (err: unknown) {
        const axiosMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        setError(axiosMessage || 'ההזמנה לא נמצאה')
      } finally {
        setLoading(false)
      }
    }
    loadInvite()
  }, [inviteUuid, navigate])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!inviteUuid) return
    setError(null)

    if (!name.trim() || !phone.trim() || !email.trim() || !cv) {
      setError('יש למלא שם, טלפון, אימייל ולהעלות קורות חיים')
      return
    }

    const extension = cv.name.split('.').pop()?.toLowerCase()
    if (!extension || !['pdf', 'doc', 'docx'].includes(extension)) {
      setError('קורות החיים צריכים להיות קובץ PDF, DOC או DOCX')
      return
    }

    setSubmitting(true)
    try {
      const response = await recruiterApi.startReadyTest(inviteUuid, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        cv,
      })
      navigate(`/candidate/${response.sent_form_uuid}`, { replace: true })
    } catch (err: unknown) {
      const axiosMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setError(axiosMessage || 'שגיאה בפתיחת המבחן')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-svh bg-background px-4 py-10" dir="rtl">
      <div className="mx-auto max-w-xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">לפני תחילת המבחן</CardTitle>
          </CardHeader>
          <CardContent>
            {jobDescription && (
              <p className="mb-4 whitespace-pre-wrap text-sm text-gray-600">{jobDescription}</p>
            )}
            {error && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">שם</Label>
                <Input id="name" value={name} onChange={(event) => setName(event.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">טלפון</Label>
                <Input id="phone" value={phone} onChange={(event) => setPhone(event.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">אימייל</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cv">קורות חיים</Label>
                <Input
                  id="cv"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(event) => setCv(event.target.files?.[0] || null)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                    שומר...
                  </>
                ) : (
                  'התחל מבחן'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
